import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    InputSingleSelect,
    ScrollBar,
    SparkIcon,
    TagColored,
    Text,
    TooltipTrigger,
    useAnchoredPanelPosition,
    useEscapeKey,
    useListNavigation,
    useOutsideClick,
    useReadOnly,
    getOverlayPortalParent,
} from '@spark';
import NoProvidersAlert from './NoProvidersAlert';
import {
    WALKTHROUGH_HINT_ITEM_CLASS,
    useDemoTourActive,
    useWalkthroughAutoOpen,
    useWalkthroughStepActive,
} from '@/features/walkthrough';
import { DEMO_TEACHER_MODEL, DEMO_TEACHER_PROVIDER } from '@/features/walkthrough/demo/demoFixtures';
import { useCanAddKeyFromModelPicker } from '@/features/auth/providerAccess';
import { analyzeCommercialProviders } from '@/features/model-setup/hooks/useHasNoCommercialProviders';
import { useCommercialModels } from '@/shared/hooks/useCommercialModels';
import { useSpecificAIModels } from '@/features/model-setup/hooks/useSpecificAIModels';
import { useCommercialModelFavorites } from '@/features/model-setup/hooks/useCommercialModelFavorites';
import { useRecommendedProvider } from '@/features/model-setup/hooks/useRecommendedProvider';
import { isGenerativeTaskLoose, isProviderAllowedForTask } from '@/shared/domain/taskTypes';
import { compareProviderSectionOrder } from '@/features/model-setup/utils/specificAIModelLabel';
import './ModelSelection.css';

const SPECIFICAI_PROVIDER = 'specificai';

const PROVIDER_LABELS = {
    openai: 'Open AI',
    bedrock: 'Bedrock',
    anthropic: 'Anthropic',
    google: 'Google',
    azure: 'AzureAI',
    vertex: 'VertexAI',
    deepseek: 'DeepSeek',
    jev: 'TypeSafe AI',
    [SPECIFICAI_PROVIDER]: 'SpecificAI',
};

const VENDOR_LABELS = {
    openai: 'Open AI',
    anthropic: 'Anthropic',
    google: 'Google',
    mistralai: 'Mistral AI',
    meta: 'Meta',
    microsoft: 'Microsoft',
    cohere: 'Cohere',
    ai21: 'AI21 Labs',
    deepseek: 'DeepSeek',
    xai: 'xAI',
    amazon: 'Amazon',
    typesafe: 'TypeSafe AI',
};

const vendorLabelFor = (vendor) => {
    if (vendor == null) return 'Other';
    return VENDOR_LABELS[String(vendor).toLowerCase()] || vendor;
};

const providerLabelFor = (provider) => PROVIDER_LABELS[provider] || provider;

/**
 * Subtitle on Favorites rows: ``Open AI`` or ``AzureAI (Open AI)``.
 */
const favoriteMetaLabel = (provider, vendor, providerType) => {
    const base = providerLabelFor(provider);
    if (providerType === 'cloud' && vendor) {
        return `${base} (${vendorLabelFor(vendor)})`;
    }
    return base;
};

/**
 * Subtitle on cloud provider section rows: ``Cloud (Open AI)``.
 */
const cloudRowMetaLabel = (vendor) => `Cloud (${vendorLabelFor(vendor)})`;

/**
 * Shared commercial / teacher model picker (Figma 3181-7666).
 *
 * One dropdown for Model Setup, Playground Add Model, and prompt modals —
 * Favorites, provider groups, recommended badge, and “Add a new key”.
 *
 * Props:
 * - value / onChange : controlled ``{ provider, model }``
 * - role             : ``Viewer`` disables the control
 * - required         : asterisk on the label
 * - labelText        : label (empty string hides it)
 * - placeholder      : trigger placeholder when nothing is selected
 * - includeSpecificAI / llmUsecaseId / clientId : Playground Add Model only.
 *                    SpecificAI trained versions (ready to deploy / deployed)
 *                    are omitted from Model configuration and prompt modals.
 * - taskType         : generative tasks hide cloud providers
 * - requireTools     : hide models the catalog probe saw reject tool calls
 *                      (``supports_tools: false``) — the Retrieval Agent
 *                      cannot run on them. A missing flag means "not probed".
 * - showEmptyAlert   : set false when the host page renders the
 *                      "add an API key" alert itself (e.g. full width)
 *
 * Deprecated no-ops kept for call-site compatibility: ``variant``, ``vendorRowVariant``.
 */
const ModelSelection = ({
    clientId,
    value,
    onChange,
    role,
    required = true,
    labelText = 'Select commercial model',
    placeholder = 'Commercial model',
    includeSpecificAI = false,
    llmUsecaseId,
    taskType,
    requireTools = false,
    showEmptyAlert = true,
    // eslint-disable-next-line no-unused-vars
    variant = 'default',
    // eslint-disable-next-line no-unused-vars
    vendorRowVariant = 'segmented',
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isCloudDisabled = isGenerativeTaskLoose(taskType);
    const {
        availableProviders,
        isLoading: isLoadingProviders,
        refresh: refreshProviders,
    } = useCommercialModels();
    const { specificAIModels } = useSpecificAIModels(
        clientId,
        llmUsecaseId,
        includeSpecificAI
    );
    const { favorites, isFavorite, toggleFavorite } = useCommercialModelFavorites();
    const { readOnly } = useReadOnly();
    const recommendedProvider = useRecommendedProvider();
    const recommendedProviderId = recommendedProvider?.provider || null;

    const rootRef = useRef(null);
    const panelRef = useRef(null);
    const scrollRef = useRef(null);
    // The "Select commercial model" walkthrough mark opens the list and dashes
    // the demo's model; the read-only demo keeps it open until that row is
    // picked (outside clicks and Escape are ignored while locked).
    const demoTour = useDemoTourActive();
    const [open, setOpen, locked] = useWalkthroughAutoOpen('commercial-model', { lockOpen: demoTour });
    const hintActive = useWalkthroughStepActive('commercial-model');
    const [searchText, setSearchText] = useState('');

    const close = useCallback(() => setOpen(false), [setOpen]);
    useOutsideClick([rootRef, panelRef], close, open && !locked);
    useEscapeKey(close, open && !locked);
    const panelStyle = useAnchoredPanelPosition(rootRef, panelRef, open);

    // Coming back from the provider-key dialog remounts this picker. Refetch
    // once when the catalog is empty and idle (the GET failed, or the Settings
    // refresh finished empty). An in-flight load is left alone so a successful
    // refresh is not issued twice; the ref stops a workspace that really has
    // no providers from refetching forever.
    const didRefetchEmptyCatalog = useRef(false);
    useEffect(() => {
        if (didRefetchEmptyCatalog.current || isLoadingProviders) return;
        if (Object.keys(availableProviders || {}).length > 0) return;
        didRefetchEmptyCatalog.current = true;
        refreshProviders?.();
    }, [availableProviders, isLoadingProviders, refreshProviders]);

    const catalogEntries = useMemo(() => {
        const entries = [];
        for (const [provider, info] of Object.entries(availableProviders || {})) {
            const providerType = info?.provider_type === 'cloud' ? 'cloud' : 'vendor';
            if (isCloudDisabled && providerType === 'cloud') continue;
            // Classification-only decision models (TypeSafe AI's Jev) are
            // hidden on every other task type.
            if (!isProviderAllowedForTask(provider, taskType)) continue;
            const models = Array.isArray(info?.models) ? info.models : [];
            for (const m of models) {
                if (!m?.id) continue;
                if (requireTools && m.supports_tools === false) continue;
                entries.push({
                    provider,
                    model: m.id,
                    label: m.id,
                    vendor: m.vendor ?? null,
                    providerType,
                    batchOnly: Boolean(m.batch_only),
                });
            }
        }
        if (includeSpecificAI) {
            for (const m of specificAIModels || []) {
                const id = m?.value ?? m?.id;
                if (!id) continue;
                entries.push({
                    provider: SPECIFICAI_PROVIDER,
                    model: id,
                    label: m.label || id,
                    vendor: null,
                    providerType: 'vendor',
                });
            }
        }
        return entries;
    }, [availableProviders, isCloudDisabled, taskType, requireTools, includeSpecificAI, specificAIModels]);

    const entryLookup = useMemo(() => {
        const map = new Map();
        for (const entry of catalogEntries) {
            map.set(`${entry.provider}::${entry.model}`, entry);
        }
        return map;
    }, [catalogEntries]);

    const provider = value?.provider || null;
    const model = value?.model ?? null;

    const favoriteEntries = useMemo(() => {
        return favorites
            .map((fav) => entryLookup.get(`${fav.provider}::${fav.model}`))
            .filter(Boolean);
    }, [favorites, entryLookup]);

    const providerSections = useMemo(() => {
        const byProvider = new Map();
        for (const entry of catalogEntries) {
            if (!byProvider.has(entry.provider)) byProvider.set(entry.provider, []);
            byProvider.get(entry.provider).push(entry);
        }

        const providers = Array.from(byProvider.keys());
        providers.sort((a, b) =>
            compareProviderSectionOrder(a, b, {
                specificAIProvider: SPECIFICAI_PROVIDER,
                recommendedProviderId,
                labelFor: providerLabelFor,
            })
        );

        return providers.map((providerId) => ({
            provider: providerId,
            label: providerLabelFor(providerId),
            isRecommended: providerId === recommendedProviderId,
            entries: byProvider.get(providerId) || [],
        }));
    }, [catalogEntries, recommendedProviderId]);

    const query = searchText.trim().toLowerCase();

    const matchesQuery = useCallback(
        (entry) => {
            if (!query) return true;
            const haystack = [
                entry.label,
                entry.model,
                providerLabelFor(entry.provider),
                entry.vendor ? vendorLabelFor(entry.vendor) : '',
                entry.batchOnly ? 'batch' : '',
            ]
                .join(' ')
                .toLowerCase();
            return haystack.includes(query);
        },
        [query]
    );

    const visibleFavorites = useMemo(
        () => favoriteEntries.filter(matchesQuery),
        [favoriteEntries, matchesQuery]
    );

    /** Sections filtered by the search text; `offset` is the row's position in
     *  the flattened keyboard-navigation order (favorites come first). */
    const visibleSections = useMemo(() => {
        let nextIndex = visibleFavorites.length;
        return providerSections
            .map((section) => ({
                ...section,
                entries: section.entries.filter(matchesQuery),
            }))
            .filter((section) => section.entries.length > 0)
            .map((section) => {
                const withOffset = { ...section, offset: nextIndex };
                nextIndex += section.entries.length;
                return withOffset;
            });
    }, [providerSections, matchesQuery, visibleFavorites.length]);

    const navEntries = useMemo(
        () => [...visibleFavorites, ...visibleSections.flatMap((s) => s.entries)],
        [visibleFavorites, visibleSections]
    );

    const { activeIndex, setActiveIndex, handleKeyDown: handleListKeyDown } =
        useListNavigation({ itemCount: navEntries.length });

    // Reset the highlight when the visible rows change or the panel closes.
    const navKey = useMemo(
        () => navEntries.map((e) => `${e.provider}::${e.model}`).join('\0'),
        [navEntries]
    );
    useLayoutEffect(() => {
        setActiveIndex(-1);
    }, [navKey, setActiveIndex]);

    useEffect(() => {
        if (!open) {
            setSearchText('');
            setActiveIndex(-1);
        }
    }, [open, setActiveIndex]);

    // Search shrinks the list; leave scrollTop at 0 so matches are not hidden
    // below a leftover offset from scrolling the full catalog. Layout phase
    // so this wins over the keyboard scrollIntoView effect below.
    useLayoutEffect(() => {
        if (open && scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [open, searchText]);

    useEffect(() => {
        if (!open || activeIndex < 0) return;
        panelRef.current
            ?.querySelector(`#model-selection-option-${activeIndex}`)
            ?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex, open]);

    const catalogStillLoading = isLoadingProviders && catalogEntries.length === 0;
    const hasNoProviders = !isLoadingProviders && catalogEntries.length === 0;
    // When the catalog is empty only because the configured provider(s) are
    // classification-only (TypeSafe AI) and this is not a classification
    // task, the empty-state alert explains that instead of "no providers".
    const classificationOnlyConfigured =
        hasNoProviders &&
        analyzeCommercialProviders(availableProviders, taskType)
            .hasClassificationOnlyProvidersExcluded;

    const selectedEntry =
        provider && model ? entryLookup.get(`${provider}::${model}`) : null;
    // When the saved pair has no catalog entry — either because the catalog is
    // still loading, or because the provider's credentials were deleted (or the
    // model was retired) — keep showing the saved model id instead of the
    // placeholder. The saved selection stays on the usecase untouched, so
    // re-adding the API key in Settings makes it fully usable again.
    const triggerText = selectedEntry
        ? String(selectedEntry.label || selectedEntry.model)
        : model && provider !== SPECIFICAI_PROVIDER
            ? String(model)
            : undefined;

    const handleSelect = useCallback(
        (entry) => {
            onChange?.({ provider: entry.provider, model: entry.model });
            // A pick closes the list even while the walkthrough locks it open.
            setOpen(false, { force: true });
        },
        [onChange, setOpen]
    );

    const handleSearchKeyDown = useCallback(
        (event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                // Home/End stay with the input for caret movement.
                handleListKeyDown(event);
                return;
            }
            if (event.key === 'Enter') {
                event.preventDefault();
                const index = activeIndex >= 0 ? activeIndex : query ? 0 : -1;
                if (index >= 0 && index < navEntries.length) {
                    handleSelect(navEntries[index]);
                }
            }
        },
        [handleListKeyDown, activeIndex, query, navEntries, handleSelect]
    );

    const handleToggleFavorite = useCallback(
        (event, entry) => {
            event.preventDefault();
            event.stopPropagation();
            toggleFavorite(entry.provider, entry.model);
        },
        [toggleFavorite]
    );

    const goToModelProvidersSettings = useCallback(() => {
        navigate('/settings?tab=providers', {
            state: { from: `${location.pathname}${location.search}${location.hash}` },
        });
    }, [navigate, location.pathname, location.search, location.hash]);

    const handleAddKey = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        goToModelProvidersSettings();
    }, [goToModelProvidersSettings]);

    const isViewer = role === 'Viewer';
    // Never on SaaS (the keys are SpecificAI's); elsewhere whoever manages providers.
    const canAddKey = useCanAddKeyFromModelPicker();

    const renderRow = (entry, { showProviderMeta = false, navIndex = -1 } = {}) => {
        const selected =
            provider === entry.provider && model === entry.model;
        const active = navIndex >= 0 && navIndex === activeIndex;
        const favorited = isFavorite(entry.provider, entry.model);
        const meta = showProviderMeta
            ? favoriteMetaLabel(entry.provider, entry.vendor, entry.providerType)
            : entry.providerType === 'cloud'
                ? cloudRowMetaLabel(entry.vendor)
                : null;
        // The dashed walkthrough hint marks the demo's model once, in its
        // provider section (a favourited copy above it is left alone).
        const hinted =
            hintActive &&
            !showProviderMeta &&
            entry.provider === DEMO_TEACHER_PROVIDER &&
            entry.model === DEMO_TEACHER_MODEL;

        return (
            <button
                key={`${entry.provider}::${entry.model}${showProviderMeta ? ':fav' : ''}`}
                id={navIndex >= 0 ? `model-selection-option-${navIndex}` : undefined}
                type="button"
                className={`model-selection__item${selected ? ' model-selection__item--selected' : ''}${active ? ' model-selection__item--active' : ''}${hinted ? ` ${WALKTHROUGH_HINT_ITEM_CLASS}` : ''}`}
                onClick={() => handleSelect(entry)}
                onMouseEnter={() => {
                    if (navIndex >= 0) setActiveIndex(navIndex);
                }}
            >
                <span className="model-selection__item-label">{entry.label || entry.model}</span>
                <span className="model-selection__item-trailing">
                    {entry.batchOnly ? (
                        <TagColored variant="cyan" label="Batch" />
                    ) : null}
                    {meta && (
                        <span className="model-selection__item-meta">{meta}</span>
                    )}
                    <TooltipTrigger
                        text={favorited ? 'Remove from favourites' : 'Add to favourites'}
                        placement="top"
                    >
                        <span
                            role="button"
                            tabIndex={0}
                            className={`model-selection__star${favorited ? ' model-selection__star--active' : ''}`}
                            aria-label={favorited ? 'Remove from favourites' : 'Add to favourites'}
                            onClick={(e) => handleToggleFavorite(e, entry)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleToggleFavorite(e, entry);
                                }
                            }}
                        >
                            <SparkIcon
                                slug={favorited ? 'star-filled' : 'star-outline'}
                                size="small"
                            />
                        </span>
                    </TooltipTrigger>
                </span>
            </button>
        );
    };

    const panel = open && !isViewer && !readOnly ? (
        createPortal(
            <div
                ref={panelRef}
                className="model-selection__panel spark-dropdown-menu-panel spark-dropdown-menu-panel--portal"
                style={panelStyle}
            >
                {canAddKey && (
                    <button
                        type="button"
                        className="model-selection__add-key"
                        onClick={handleAddKey}
                    >
                        Can&apos;t find it? Add a new key
                    </button>
                )}

                <div className="model-selection__list-shell">
                    <div ref={scrollRef} className="model-selection__panel-scroll">
                        {visibleFavorites.length > 0 && (
                            <div className="model-selection__section">
                                <Text
                                    variant="detail-semibold"
                                    as="div"
                                    className="model-selection__section-title"
                                >
                                    Favorites
                                </Text>
                                {visibleFavorites.map((entry, index) =>
                                    renderRow(entry, {
                                        showProviderMeta: true,
                                        navIndex: index,
                                    })
                                )}
                            </div>
                        )}

                        {visibleSections.map((section) => (
                            <div key={section.provider} className="model-selection__section">
                                <div className="model-selection__section-header">
                                    <Text
                                        variant="detail-semibold"
                                        as="div"
                                        className="model-selection__section-title"
                                    >
                                        {section.label}
                                    </Text>
                                    {section.isRecommended && (
                                        <TagColored
                                            variant="pink"
                                            label="Recommended by admin"
                                        />
                                    )}
                                </div>
                                {section.entries.map((entry, index) =>
                                    renderRow(entry, { navIndex: section.offset + index })
                                )}
                            </div>
                        ))}

                        {!hasNoProviders && navEntries.length === 0 && (
                            <Text variant="base-regular" color="secondary" as="div" aria-live="polite">
                                {catalogStillLoading
                                    ? 'Loading models…'
                                    : query
                                      ? 'No models match your search'
                                      : 'No models available'}
                            </Text>
                        )}
                    </div>
                    <ScrollBar
                        containerRef={scrollRef}
                        className="model-selection__scrollbar"
                    />
                </div>
            </div>,
            getOverlayPortalParent(rootRef.current)
        )
    ) : null;

    return (
        <div className="fs-unmask model-selection">
            {hasNoProviders && showEmptyAlert && (
                <div className="model-selection__empty-alert">
                    <NoProvidersAlert
                        classificationOnlyConfigured={classificationOnlyConfigured}
                    />
                </div>
            )}

            <div ref={rootRef}>
                <InputSingleSelect
                    className="model-selection__trigger"
                    size="medium"
                    label={labelText || 'Select commercial model'}
                    showLabel={Boolean(labelText)}
                    showAsterisk={required && Boolean(labelText)}
                    placeholder={catalogStillLoading && !triggerText ? 'Loading models…' : placeholder}
                    text={triggerText}
                    disabled={readOnly || isViewer || hasNoProviders}
                    open={open}
                    onToggle={() => {
                        if (!isViewer && !hasNoProviders) setOpen((prev) => !prev);
                    }}
                    editable
                    searchValue={searchText}
                    onSearchChange={setSearchText}
                    onSearchKeyDown={handleSearchKeyDown}
                    activeDescendantId={
                        activeIndex >= 0
                            ? `model-selection-option-${activeIndex}`
                            : undefined
                    }
                />
            </div>

            {panel}
        </div>
    );
};

export default ModelSelection;
